/**
 * Purpose: Setup screen for Classroom mode (pureClassroom) — no dice, just questions.
 * Owns: Classroom-specific setup layout, mode summary, player form, game start flow.
 * Notes: This is an orchestrator page (CS-17). Sets the game mode to 'pureClassroom'.
 *        Classroom mode has no dice or seat movement, but still allows schedule
 *        mode selection for class order preference.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import { PlayerForm } from '../../components/setup/PlayerForm';
import { ModeSummary } from '../../components/setup/ModeSummary';
import { SchedulePicker } from '../../components/setup/SchedulePicker';
import familyRoadTripPack from '../../data/packs/base';
import { REGULAR_SUBJECTS } from '../../config/classConfig';
import { getModeById } from '../../config/modeConfig';
import type { Major } from '../../types/game';
import type { AgeGroup, Category, Tier } from '../../types/question';
import type { ScheduleMode } from '../../types/game';

const MODE = getModeById('pureClassroom');

export function ClassroomSetupPage() {
  const navigate = useNavigate();
  const {
    state,
    addPlayer,
    removePlayer,
    setGameMode,
    setScheduleMode,
    startGame,
    loadPack,
  } = useGame();

  const [customSubjectOrder, setCustomSubjectOrder] = useState<Category[]>(
    () => [...REGULAR_SUBJECTS],
  );

  useEffect(() => {
    setGameMode('pureClassroom');
    loadPack(familyRoadTripPack);
  }, [setGameMode, loadPack]);

  function handleAddPlayer(name: string, ageGroup: AgeGroup, tier: Tier, major: Major) {
    addPlayer(name, ageGroup, tier, major);
  }

  function handleStartGame() {
    const subjectOrder =
      state.scheduleMode === 'choose' ? customSubjectOrder : undefined;
    startGame(subjectOrder);
    navigate('/play');
  }

  return (
    <div className="chalkboard-bg" style={styles.page}>
      <button style={styles.backButton} onClick={() => navigate('/')}>
        &larr; Back
      </button>

      <h1 className="chalk-text-bright" style={styles.heading}>
        Classroom Setup
      </h1>

      <ModeSummary modeLabel={MODE.label} summaryPoints={MODE.summaryPoints} />

      {/* Schedule mode */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Schedule</h3>
        <div style={styles.optionRow}>
          {SCHEDULE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              style={{
                ...styles.scheduleChip,
                ...(state.scheduleMode === opt.value ? styles.scheduleChipActive : {}),
              }}
              onClick={() => setScheduleMode(opt.value)}
              aria-pressed={state.scheduleMode === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {state.scheduleMode === 'choose' && (
        <SchedulePicker
          subjects={customSubjectOrder}
          onReorder={setCustomSubjectOrder}
        />
      )}

      <PlayerForm
        players={state.players}
        onAddPlayer={handleAddPlayer}
        onRemovePlayer={removePlayer}
        onStartGame={handleStartGame}
      />
    </div>
  );
}

const SCHEDULE_OPTIONS: { value: ScheduleMode; label: string }[] = [
  { value: 'random', label: 'Random' },
  { value: 'choose', label: 'Pick Your Schedule' },
];

const styles: Record<string, React.CSSProperties> = {
  page: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem',
    overflowY: 'auto',
  },
  backButton: {
    alignSelf: 'flex-start',
    padding: '0.5rem 1rem',
    fontSize: '0.9rem',
    color: 'var(--text-light)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    opacity: 0.7,
  },
  heading: {
    fontSize: '1.8rem',
    fontWeight: '400',
    textAlign: 'center' as const,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    maxWidth: '480px',
    width: '100%',
    margin: '0 auto',
    padding: '0 1rem',
  },
  sectionTitle: {
    fontSize: '0.85rem',
    fontWeight: '700',
    color: 'var(--muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    margin: 0,
  },
  optionRow: {
    display: 'flex',
    gap: '0.5rem',
  },
  scheduleChip: {
    flex: 1,
    padding: '0.65rem',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-light)',
    fontSize: '0.9rem',
    fontWeight: '600',
    border: '2px solid transparent',
    cursor: 'pointer',
    textAlign: 'center' as const,
  },
  scheduleChipActive: {
    border: '2px solid var(--accent-gold)',
  },
};
